import React, { useState } from 'react';
import { FiShield, FiAlertTriangle, FiClock } from 'react-icons/fi';

const RisksTab = ({ insightData, Section }) => {
  const [riskExpanded, setRiskExpanded] = useState(true);
  const onToggleRisk = React.useCallback(() => setRiskExpanded(v => !v), []);
  const [earlyExpanded, setEarlyExpanded] = useState(false);
  const onToggleEarly = React.useCallback(() => setEarlyExpanded(v => !v), []);
  const [mitigationExpanded, setMitigationExpanded] = useState(false);
  const onToggleMitigation = React.useCallback(() => setMitigationExpanded(v => !v), []);

  if (!insightData?.heptapod_dimensional_analysis?.risk_intelligence) return null;

  const riskIntel = insightData.heptapod_dimensional_analysis.risk_intelligence;

  return (
    <>
      {/* Risk Intelligence */}
      <Section title="Risk Assessment" iconComponent={FiShield} collapsible expanded={riskExpanded} onToggle={onToggleRisk}>
        {riskExpanded && (
          <div className="space-y-4">
            {riskIntel.risk_assessment?.map((risk, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                <div className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    risk.severity_level === 'high' ? 'bg-red-100' : 
                    risk.severity_level === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                  }`}>
                    <span className={`text-sm ${
                      risk.severity_level === 'high' ? 'text-red-600' : 
                      risk.severity_level === 'medium' ? 'text-yellow-600' : 'text-green-600'
                    }`}>⚠️</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`text-sm font-semibold mb-2 ${
                        risk.severity_level === 'high' ? 'text-red-700' : 
                        risk.severity_level === 'medium' ? 'text-yellow-700' : 'text-green-700'
                      }`}>
                        {risk.severity_level?.toUpperCase()} RISK
                      </h4>
                      <span className="text-xs text-gray-500">
                        {risk.probability}% probability
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 leading-relaxed mb-3">
                      {risk.description}
                    </div>
                    {risk.root_causes && (
                      <div className="text-xs text-gray-500">
                        <strong>Root Causes:</strong> {risk.root_causes.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Early Warning Indicators */}
      {riskIntel.early_warning_indicators && (
        <Section title="Early Warning Indicators" iconComponent={FiAlertTriangle} collapsible expanded={earlyExpanded} onToggle={onToggleEarly}>
          {earlyExpanded && (
            <div className="space-y-4">
              {riskIntel.early_warning_indicators.map((indicator, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-gray-600 text-sm">📊</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          {indicator.indicator}
                        </h4>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          indicator.current_status === 'green' ? 'bg-green-100 text-green-800' : 
                          indicator.current_status === 'yellow' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {indicator.current_status}
                        </span>
                      </div>
                      
                      {indicator.threshold_values && (
                        <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                          <div className="text-center">
                            <div className="text-gray-500 mb-1">Green</div>
                            <div className="text-green-700 font-semibold">{indicator.threshold_values.green}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-500 mb-1">Yellow</div>
                            <div className="text-yellow-700 font-semibold">{indicator.threshold_values.yellow}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-500 mb-1">Red</div>
                            <div className="text-red-700 font-semibold">{indicator.threshold_values.red}</div>
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        Monitoring: {indicator.monitoring_frequency}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Mitigation Strategies */}
      {riskIntel.mitigation_strategies && (
        <Section title="Mitigation Strategies" iconComponent={FiShield} collapsible expanded={mitigationExpanded} onToggle={onToggleMitigation}>
          {mitigationExpanded && (
            <div className="space-y-4">
              {riskIntel.mitigation_strategies.map((strategy, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-green-600 text-sm">🛡️</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Risk {strategy.risk_id}
                        </h4>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                          {strategy.effectiveness_probability}% Effective
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-700 leading-relaxed mb-4">
                        {strategy.strategy}
                      </div>
                      
                      {strategy.actions && (
                        <div>
                          <div className="text-xs font-semibold text-gray-700 mb-3">
                            Actions:
                          </div>
                          {strategy.actions.map((action, actionIdx) => (
                            <div key={actionIdx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                              <div className="text-xs text-gray-700 mb-2">
                                {action.action}
                              </div>
                              <div className="flex justify-between text-xs text-gray-500">
                                <span><strong>Owner:</strong> {action.owner}</span>
                                <span><strong>Timeline:</strong> {action.timeline}</span>
                              </div>
                              {action.success_metric && (
                                <div className="text-xs text-green-600 mt-2">
                                  <strong>Success:</strong> {action.success_metric}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </>
  );
};

export default RisksTab;
